import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import ExcelJS from 'exceljs';
// import { reviewTypeLocation } from './timeline-calculator.constant';
// import { ReviewType } from './timeline-calculator.service';
import {
  GlrrActivity,
  GLRR_TIMELINE_ACTIVITIES_MULTI,
  GLRR_TIMELINE_ACTIVITIES_SINGLE,
  reviewTypeLocation,
  StdActivity,
  STD_TIMELINE_ACTIVITIES_MULTI,
  STD_TIMELINE_ACTIVITIES_SINGLE,
} from './timeline-calculator.constant';

interface ReviewOption {
  value: string;
  label: string;
  title: string;
  reviewType: string;
  locationType: string;
  isMultiLocation: boolean;
  isGoLive: boolean;
}

interface ReviewOptionGroup {
  label: string;
  options: ReviewOption[];
}

interface TimelineResult {
  activity: string;
  date: Date;
  entered?: boolean;
}

@Component({
  selector: 'app-timeline-calculator',
  imports: [ReactiveFormsModule],
  templateUrl: './timeline-calculator.component.html',
  styleUrl: './timeline-calculator.component.css',
})
export class TimelineCalculatorComponent {
  private readonly fb = inject(FormBuilder);

  readonly reviewOptionGroups: ReviewOptionGroup[] = reviewTypeLocation.map(group => ({
    label: group.label,
    options: group.options.map(option => ({
      value: `${option.reviewType}-${option.locationType}`,
      label: option.label,
      title: option.label,
      reviewType: option.reviewType,
      locationType: option.locationType,
      isMultiLocation: option.locationType === 'Multiple',
      isGoLive: option.reviewType === 'Go-Live',
    })),
  }));

  readonly reviewOptions: ReviewOption[] = this.reviewOptionGroups.flatMap(group => group.options);

  readonly form: FormGroup = this.fb.group(
    {
      selectedReview: ['', Validators.required],
      fieldworkStartDate: ['', Validators.required],
      fieldworkEndDate: ['', Validators.required],
    }
  );

  results: TimelineResult[] | null = null;
  calculatedOn: Date | null = null;

  get showDateFields(): boolean {
    return !!this.form.get('selectedReview')?.value;
  }

  get hasInvalidDateRange(): boolean {
    return this.isDateRangeInvalid() && !!this.form.get('fieldworkEndDate')?.touched;
  }

  get selectedOption(): ReviewOption | undefined {
    return this.reviewOptions.find(option => option.value === this.form.get('selectedReview')?.value);
  }

  get startDateLabel(): string {
    return this.selectedOption?.isGoLive ? 'Go-Live Date' : 'Fieldwork Start Date';
  }

  get endDateLabel(): string {
    return this.selectedOption?.isGoLive ? 'Post Go-Live Testing End Date' : 'Fieldwork End Date';
  }

  get dateRangeError(): string {
    return 'End Date must be on or after Start Date';
  }

  onReviewChange(): void {
    this.form.patchValue({ fieldworkStartDate: '', fieldworkEndDate: '' });
    this.results = null;
    this.calculatedOn = null;
  }

  calculate(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isDateRangeInvalid()) {
      this.form.markAllAsTouched();
      return;
    }

    const { fieldworkStartDate, fieldworkEndDate } = this.form.value;
    const option = this.selectedOption!;
    console.log(option.reviewType, option.locationType, fieldworkStartDate, fieldworkEndDate);
    this.finalCalculation(option.reviewType, option.locationType, fieldworkStartDate, fieldworkEndDate);
  }

    private finalCalculation(
    rawReviewType: string,
    rawLocationType: string,
    fieldworkStartDate: string,
    fieldworkEndDate: string
  ): void {
    const reviewType = rawReviewType.toLowerCase();

    let reviewDetails: readonly StdActivity[] | readonly GlrrActivity[];

    if ((reviewType === 'standard' || reviewType === 'on-demand' || reviewType === 'special project') && rawLocationType === 'Single') {
      reviewDetails = STD_TIMELINE_ACTIVITIES_SINGLE;
    } else if ((reviewType === 'standard' || reviewType === 'on-demand' || reviewType === 'special project') && rawLocationType === 'Multiple') {
      reviewDetails = STD_TIMELINE_ACTIVITIES_MULTI;
    } else if (reviewType === 'go-live' && rawLocationType === 'Single') {
      reviewDetails = GLRR_TIMELINE_ACTIVITIES_SINGLE;
    } else if (reviewType === 'go-live' && rawLocationType === 'Multiple') {
      reviewDetails = GLRR_TIMELINE_ACTIVITIES_MULTI;
    } else {
      return;
    }

    const startDate = this.parseLocalDate(fieldworkStartDate)!;
    const endDate   = this.parseLocalDate(fieldworkEndDate)!;

    const calculatedEntries = (reviewDetails as readonly (StdActivity | GlrrActivity)[]).map(activity => {
      // const isStartAnchor = activity.anchor === 'start' || activity.anchor === 'end';
      const exactDate     = activity.anchor === 'start' ? startDate : endDate;

      const calculatedDate =
        activity.offset !== null
          ? this.adjustToMonday(this.addWorkingDays(exactDate, activity.offset))
          : new Date(exactDate.getTime());

      return {
        label: activity.label,
        calculatedDate,
        isUserEntered: activity.isUserEntered ?? false,
      };
    });

    this.results = calculatedEntries.map(entry => ({
      activity: entry.label,
      date: entry.calculatedDate,
      entered: entry.isUserEntered,
    }));
    this.calculatedOn = new Date();
  }

  private isDateRangeInvalid(): boolean {
    const start = this.form.get('fieldworkStartDate')?.value;
    const end = this.form.get('fieldworkEndDate')?.value;
    return !!start && !!end && new Date(end) < new Date(start);
  }

  private addWorkingDays(start: Date, days: number): Date {
    const result = new Date(start.getTime());
    const step = days < 0 ? -1 : 1;
    let remaining = Math.abs(days);
    while (remaining > 0) {
      result.setDate(result.getDate() + step);
      const day = result.getDay();
      if (day !== 0 && day !== 6) remaining--;
    }
    return result;
  }

  private adjustToMonday(date: Date): Date {
    const result = new Date(date.getTime());
    const day = result.getDay();
    if (day === 6) result.setDate(result.getDate() + 2);
    if (day === 0) result.setDate(result.getDate() + 1);
    return result;
  }
 

  clear(): void {
    this.form.reset({ selectedReview: '', fieldworkStartDate: '', fieldworkEndDate: '' });
    this.results = null;
    this.calculatedOn = null;
  }

  async exportToXlsx(): Promise<void> {
    if (!this.results || !this.selectedOption) return;

    const today = this.formatDate(new Date());
    const reviewType = this.selectedOption.title;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Timeline Calculator');

    // Row 1: Review Type + Calculation Date (bold)
    const row1 = worksheet.addRow([reviewType, `Calculated on ${today}`]);
    row1.getCell(1).font = { bold: true };
    row1.getCell(2).font = { bold: true };

    // Empty row
    worksheet.addRow([]);

    // Input values
    const { fieldworkStartDate, fieldworkEndDate } = this.form.value;
    if (this.selectedOption.isGoLive) {
      worksheet.addRow(['Go-Live Date:', this.formatDate(this.parseLocalDate(fieldworkStartDate)!)]);
      worksheet.addRow(['Post Go-Live Testing End Date:', this.formatDate(this.parseLocalDate(fieldworkEndDate)!)]);
    } else {
      worksheet.addRow(['Fieldwork Start Date:', this.formatDate(this.parseLocalDate(fieldworkStartDate)!)]);
      worksheet.addRow(['Fieldwork End Date:', this.formatDate(this.parseLocalDate(fieldworkEndDate)!)]);
    }

    // Empty row
    worksheet.addRow([]);

    // Column headers (bold)
    const headerRow = worksheet.addRow(['Activity Name', 'Calculated Date']);
    headerRow.getCell(1).font = { bold: true };
    headerRow.getCell(2).font = { bold: true };

    // Activity rows
    this.results.forEach(result => {
      worksheet.addRow([result.activity, this.formatDate(result.date)]);
    });

    worksheet.getColumn(1).width = this.selectedOption.isGoLive ? 72 : 50;
    worksheet.getColumn(2).width = 24;

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeReviewType = reviewType.replace(/[^a-zA-Z0-9]/g, '_');
    link.href = url;
    link.download = `AST_Timeline_Calculator_${safeReviewType}_${today.replace(/-/g, '')}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }

  formatDate(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
  }

  formatPickerDate(value: string | null | undefined): string {
    if (!value) return '';
    const [year, month, day] = value.split('-').map(Number);
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    if (!year || month < 1 || month > 12 || !day) return '';
    return `${String(day).padStart(2, '0')}-${months[month - 1]}-${year}`;
  }

  openDatePicker(input: HTMLInputElement): void {
    const picker = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof picker.showPicker === 'function') picker.showPicker();
    else input.focus();
  }

  private parseLocalDate(value: string): Date | null {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}

